/*
 * How the loop's calls travel.
 *
 * The loop never knows where the tree lives. A server worker hands it the Durable Object stub
 * directly — it is already inside the process that owns the tree, so there is nothing to serialize
 * over. A browser hands it `HttpDriverHost`, which posts the same protobuf bytes to the same four
 * routes. Nothing downstream can tell which advanced a run, which is the property that makes
 * "browser now, server later" a deployment decision instead of a second implementation.
 */

/** The four verbs, and the whole of what the loop needs. Declared structurally rather than as a
 *  class, so it can be satisfied by a DO stub, an HTTP client, or a fake in a test. */
export interface DriverHost {
  lease(b: Uint8Array): Promise<Uint8Array>;
  applyRun(b: Uint8Array): Promise<Uint8Array>;
  ownerAct(b: Uint8Array): Promise<Uint8Array>;
  treeView(b: Uint8Array): Promise<Uint8Array>;
}

export interface HttpDriverHostOptions {
  /** Same-origin in both dev and prod, so there is no CORS surface to reason about. */
  baseUrl?: string;
  /** The live session token. Called per request rather than captured once, because a tab outlives
   *  any single token and a driver that cached one would start failing mid-run at expiry. */
  token: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

/** The browser's transport to the `/agent/*` routes. Protobuf on the wire, same as the DO stub. */
export class HttpDriverHost implements DriverHost {
  private readonly base: string;
  private readonly token: () => Promise<string | null>;
  private readonly doFetch: typeof fetch;

  constructor(opts: HttpDriverHostOptions) {
    this.base = (opts.baseUrl ?? '').replace(/\/$/, '');
    this.token = opts.token;
    this.doFetch = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  lease = (b: Uint8Array) => this.post('/agent/lease', b);
  applyRun = (b: Uint8Array) => this.post('/agent/run', b);
  ownerAct = (b: Uint8Array) => this.post('/agent/owner', b);
  treeView = (b: Uint8Array) => this.post('/agent/tree', b);

  private async post(path: string, body: Uint8Array): Promise<Uint8Array> {
    const token = await this.token();
    const res = await this.doFetch(`${this.base}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-protobuf',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body as BodyInit,
    });
    // A transport failure is thrown, never decoded. Every reply on these routes carries a `Result`,
    // so an empty body decoded as "success with no error" would be a refusal the caller cannot see —
    // and on the paid paths that is a driver believing it may keep spending.
    if (!res.ok) {
      // Said for a person: a status alone is what the console already shows. An empty status text is
      // the shape of a proxy answering for a service that is not there.
      const what = res.statusText ? `${res.status} ${res.statusText}` : `HTTP ${res.status}`;
      throw new Error(`The AI service answered ${what} for ${path} — it may be down or restarting; this will retry.`);
    }
    return new Uint8Array(await res.arrayBuffer());
  }
}


/*
 * ── Credits: reserve before, settle after ───────────────────────────────────────────────────────
 *
 * The shape exists because report-after-the-fact cannot be made safe. The browser drives the loop
 * and, for the interactive session, talks straight to the provider — so the server never sees those
 * tokens. A user wanting free usage does not need a bug in our accounting; they can simply not
 * report. No amount of care in the reporting path closes that, because the reporting path is the
 * thing being trusted.
 *
 * Reserving inverts it: a turn that cannot be paid for never happens. Unmetered usage stops being
 * something we detect late and becomes something the loop structurally cannot do.
 *
 * IMPLEMENTATIONS LIVE OUTSIDE THIS PACKAGE, and must be server-side. A browser-implemented
 * `Credits` is theatre — the caller being metered would be the one deciding it could afford it.
 */
export interface CreditReservation {
  /** Handed back on settle. The natural idempotency key downstream, so a retried settle cannot
   *  double-charge — which is the property Stripe's meter events are keyed on. */
  id: string;
  /** What was held. Settling for less releases the difference; settling for more is allowed and
   *  logged, because a model that overran is a real event and refusing to record it would simply
   *  lose the money. */
  reservedUsd: number;
}

export interface Credits {
  /**
   * Hold funds for one step, or refuse it.
   *
   * `null` means REFUSED, and the loop ends the run at `TERMINAL_REASON_NO_CREDITS` before spending
   * anything. Refusal is a normal outcome with a name, not an error — "your account is empty" is a
   * different thing from "this agent hit its ceiling", and a user can act on the difference.
   */
  reserve(runId: string, estimateUsd: number): Promise<CreditReservation | null>;
  /**
   * Close out a hold. **Takes no amount, and that is the entire point.**
   *
   * It used to take `actualUsd`, sourced from the step result — which comes from the provider VIA
   * THE BROWSER. An adversarial test made the consequence concrete: a client reporting `costUsd: 0`
   * ran twelve steps on a ten-cent balance and paid nothing, with the reservation mechanism working
   * exactly as designed. Reserving bounds CONCURRENT exposure; it cannot make a client-supplied
   * figure true, and no care inside the loop can, because the loop is the thing being lied to.
   *
   * So the loop no longer says what a step cost. It says the step is over. The implementation —
   * which is server-side, and which saw the provider's response through the key-inject edge or
   * confirms it against the provider's usage API — decides the charge. There is no number here for a
   * client to falsify because it is never asked for one.
   *
   * What the client reports keeps its job: it is the live estimate the performance panel renders,
   * and it is never billed.
   *
   * Called even when a step throws, so a crashed turn cannot strand a hold — and an implementation
   * should charge rather than release on error, since a provider that died mid-call may well have
   * billed us anyway.
   */
  settle(reservation: CreditReservation): Promise<void>;
}
